import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  User,
  Settings,
  BarChart3,
  Activity,
  IndianRupee,
  CreditCard,
  PieChart,
  CheckCircle,
  Plus,
  Trash2,
  Lock,
  HelpCircle,
  Database,
  TrendingUp,
  Bell,
  LogOut,
  Menu,
  AlertCircle,
  Server,
  FileText,
  Briefcase,
  Heart,
  MessageSquare
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { NotificationContext } from "../context/NotificationContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import authAxios from "../api/authAxios";
import DashboardStats from "../components/SuperAdmin/DashboardStats";
import UserManagement from "../components/SuperAdmin/UserManagement";
import SubscriptionManagement from "../components/SuperAdmin/SubscriptionManagement";
import GlobalReports from "../components/SuperAdmin/GlobalReports";
import SecurityLogs from "../components/SuperAdmin/SecurityLogs";
import SupportTickets from "../components/SuperAdmin/SupportTickets";
import DataControls from "../components/SuperAdmin/DataControls";
import SuperAdminProfile from "../components/SuperAdmin/SuperAdminProfile";
import RolesPermissionsManagement from "../components/SuperAdmin/RolesPermissionsManagement";
import LeadsManagementPage from "./SuperAdmin/LeadsManagementPage";
import ContactLeads from "./SuperAdmin/ContactLeads";
import SystemNotificationBell from "../components/SuperAdmin/SystemNotificationBell";
import SystemNotificationsPage from "../components/SuperAdmin/SystemNotificationsPage";
import RecentActivityPage from "../components/SuperAdmin/RecentActivityPage";
import FAQManagement from "../components/SuperAdmin/FAQManagement";
import ContentManagement from "../components/SuperAdmin/ContentManagement";
import BusinessTypeManagement from "../components/SuperAdmin/BusinessTypeManagement";
import TestimonialManagement from "../components/SuperAdmin/TestimonialManagement";
import BackupManagement from "../components/SuperAdmin/BackupManagement";
import UnitManagement from "../components/SuperAdmin/UnitManagement";
import TaxManagement from "../components/SuperAdmin/TaxManagement";
import SystemSettings from "../components/SuperAdmin/SystemSettings";
import RazorpayConfig from "../components/SuperAdmin/RazorpayConfig";
import AccountRequestsPage from "./AccountRequestsPage";
import { Scale, Percent, ClipboardList } from "lucide-react";



const TABS = [
  { id: "dashboard", label: "Dashboard", icon: BarChart3 },
  { id: "profile", label: "Profile", icon: User },
  { id: "settings", label: "System Settings", icon: Settings },
  { id: "razorpay_config", label: "Razorpay Config", icon: CreditCard },
  { id: "leads", label: "Leads", icon: Users },
  { id: "contact_leads", label: "Contact Us Leads", icon: MessageSquare },
  { id: "users", label: "User Management", icon: Users },
  { id: "account_requests", label: "Account Requests", icon: ClipboardList },
  { id: "roles_permissions", label: "Roles & Permissions", icon: Lock },
  { id: "units", label: "Units", icon: Scale },
  { id: "taxes", label: "Tax Rates", icon: Percent },
  { id: "backup", label: "Backup", icon: Database },
  { id: "business_types", label: "Business Types", icon: Briefcase },
  { id: "subscriptions", label: "Subscriptions", icon: IndianRupee },
  { id: "testimonials", label: "Testimonials", icon: Heart },
  { id: "faqs", label: "FAQ Management", icon: HelpCircle },
  { id: "content", label: "Contacts", icon: FileText },
  { id: "reports", label: "Reports", icon: TrendingUp },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "activity", label: "Recent Activity", icon: Activity },
  { id: "support", label: "Support", icon: HelpCircle },
  { id: "data", label: "Data Controls", icon: Database },
];
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.2 } },
};

export default function SuperAdminPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { addNotification } = useContext(NotificationContext);
  
  // Initialize tab from URL or default to dashboard
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Sync state if URL changes (e.g. browser back button)
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
    setMobileMenuOpen(false);
  };

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Verify Super Admin access
  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate("/login");
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardStats />;
      case "settings":
        return <SystemSettings />;
      case "razorpay_config":
        return <RazorpayConfig />;
      case "profile":
        return <SuperAdminProfile />;
      case "users":
        return <UserManagement />;
      case "roles_permissions":
        return <RolesPermissionsManagement />;
      case "leads":
        return <LeadsManagementPage />;
      case "contact_leads":
        return <ContactLeads />;
      case "subscriptions":
        return <SubscriptionManagement />;
      case "testimonials":
        return <TestimonialManagement />;
      case "reports":
        return <GlobalReports />;
      case "notifications":
        return <SystemNotificationsPage />;
      case "activity":
        return <RecentActivityPage />;
      case "support":
        return <SupportTickets />;
      case "faqs":
        return <FAQManagement />;
      case "business_types":
        return <BusinessTypeManagement />;
      case "units":
        return <UnitManagement />;
      case "taxes":
        return <TaxManagement />;
      case "content":

        return <ContentManagement />;
      case "data":
        return <DataControls />;
      case "backup":
        return <BackupManagement />;
      case "account_requests":
        return <AccountRequestsPage />;
      default:
        return <DashboardStats />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 transition-colors duration-300">
      {/* Header - Simplified */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-700 transition-colors">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">Super Admin</h1>
                <p className="text-xs text-slate-400">System Master Control</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <SystemNotificationBell />

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Sidebar Navigation - Full Height */}
        <div className={`fixed lg:static top-[73px] left-0 w-64 h-[calc(100vh-73px)] ${mobileMenuOpen ? "block" : "hidden"} lg:block z-30 bg-white dark:bg-slate-800/95 backdrop-blur border-r border-slate-200 dark:border-slate-700 overflow-y-auto transition-colors`}>
          <nav className="flex flex-col min-h-full space-y-2 p-4 pb-0">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition font-medium text-sm ${isActive
                    ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700"
                    }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span>{tab.label}</span>
                </button>
              );
            })}

            {/* User Info & Logout - At Bottom */}
            <div className="sticky bottom-0 left-0 right-0 py-4 mt-auto bg-white dark:bg-slate-800/95 border-t border-slate-200 dark:border-slate-700 transition-colors z-10 w-full">
              <div className="mb-3 px-2">
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.first_name || "Super Admin"}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.phone}</p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-red-600/10 dark:bg-red-600/20 text-red-600 dark:text-red-400 hover:bg-red-600/20 dark:hover:bg-red-600/40 rounded-lg transition-all font-semibold shadow-sm shadow-red-500/10"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto w-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {renderTabContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
